import React, { FunctionComponent, useRef, useState } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { useIntl } from 'react-intl'
import { NativeSyntheticEvent, StyleProp, TextInput, TextInputSelectionChangeEventData, TextStyle } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { CODE_MESSAGES, CODE_STATUS } from '@lyrahealth-inc/shared-app-logic'

import { BodyText, Size } from '../../atoms/bodyText/BodyText'
import { AlertLoudIcon } from '../../atoms/icons'
import { AccessibilityRolesNative, IS_WEB } from '../../constants'
import { BodyTextSize, getFontStyles, TextType } from '../../styles'
import { tID } from '../../utils'
import { ThemeType } from '../../utils/themes/ThemeProvider'

interface CodeInputProps {
  codeStatus?: CODE_STATUS
  error?: string
  label?: string
  length: number
  name: string
  shouldAutoSubmit?: boolean
  value?: string
  onCodeChange?: (code: string) => void
  onSubmit: (code: string) => Promise<void>
  onAutoSubmit?: () => void
}

const Container = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  marginTop: theme.spacing['16px'],
  marginBottom: theme.spacing['16px'],
}))

const LabelContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: theme.spacing['8px'],
}))

const InputContainer = styled.View({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
})

const ErrorContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: theme.spacing['8px'],
}))

const ErrorText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.spacing['8px'],
}))

const StyledTextInput = styled.TextInput<{ error: boolean; isFocused: boolean; theme: ThemeType }>(
  ({ error, isFocused, theme }) => ({
    width: theme.breakpoints.isMinWidthMobileM ? 80 : 48,
    height: 48,
    textAlign: 'center',
    marginHorizontal: 5,
    paddingVertical: 12,
    paddingHorizontal: theme.breakpoints.isMinWidthMobileM ? 16 : 8,
    borderColor: theme.colors.borderDefault,
    borderWidth: 1,
    borderRadius: 8,
    ...(isFocused && {
      borderWidth: '2px',
      borderColor: theme.colors.borderFocus,
      outlineStyle: 'none',
    }),
    ...(error && {
      borderColor: theme.colors.borderError,
    }),
  }),
)

export const CodeInput: FunctionComponent<CodeInputProps> = ({
  codeStatus,
  error,
  label,
  length,
  name,
  shouldAutoSubmit = false,
  value = '',
  onCodeChange,
  onSubmit,
  onAutoSubmit,
}) => {
  const { colors } = useTheme() as ThemeType
  const fontStyles = getFontStyles(colors)
  const { formatMessage } = useIntl()

  const inputRefs = useRef<Array<TextInput | null>>([])
  const cursorPositionRef = useRef<{ [index: number]: { start: number; end: number } }>({})
  const inputFocusRef = useRef(false)
  const [code, setCode] = useState<string[]>(Array.from({ length }, (_, i) => value[i] || ''))
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)

  const submitCodeAndTriggerAutoSubmit = async (code: string) => {
    try {
      onCodeChange?.(code)
      // RFF calls handleSubmit using the current form state. setTimeout ensures state change is processed before submitting the form
      await new Promise((resolve) => setTimeout(resolve, 0))
      await Promise.resolve(onSubmit(code))
      onAutoSubmit?.()
    } catch (error) {
      console.error('Error triggering auto submit in CodeInput: ', error)
    }
  }

  const updateCode = (newCode: string[], index: number) => {
    const updatedCode = [...code]
    updatedCode[index] = newCode[index] || ''
    setCode(updatedCode)

    if (shouldAutoSubmit && index === length - 1 && updatedCode.length === length) {
      inputRefs.current[index]?.blur()
      submitCodeAndTriggerAutoSubmit(updatedCode.join(''))
    } else {
      onCodeChange?.(updatedCode.join(''))
      focusNextEmptyIndex(updatedCode, index + 1)
    }
  }

  const focusNextEmptyIndex = (code: string[], index: number): void => {
    if (code.length === length && !code.some((char) => char !== '')) {
      inputRefs.current[index - 1]?.blur()
      return
    }
    requestAnimationFrame(() => {
      inputRefs.current[index]?.focus()
    })
  }

  const handleOverwrite = (text: string, index: number) => {
    if (text.length < 2) return

    // Find index of new value since cursor could be to the left or right of character
    const newValue = code[index] === text[0] ? text[1] : text[0]
    const newCode = [...code]
    newCode[index] = newValue
    updateCode(newCode, index)
  }

  const handlePaste = (text: string, index: number) => {
    const newCode = [...code]
    for (let i = 0; i < text.length; i++) {
      const targetIndex = index + i
      if (targetIndex >= length) break

      newCode[targetIndex] = text[i]
    }

    setCode(newCode)

    if (shouldAutoSubmit && newCode.length === length) {
      inputRefs.current[index]?.blur()
      submitCodeAndTriggerAutoSubmit(newCode.join(''))
    } else {
      onCodeChange?.(newCode.join(''))
      focusNextEmptyIndex(newCode, index + 1)
    }
  }

  const handleChange = (text: string, index: number) => {
    // Remove whitespaces - native mobile adds a space between the existing text input value and pasted text
    text = text.replace(/\s+/g, '')

    if (!/^[a-zA-Z0-9]+$/.test(text)) return // Only allow alphanumeric characters

    // If pasted value is a full code, then start at first index regardless of cursor position
    if (text.length >= length) {
      const fullCode = text.slice(0, length)
      setCode(fullCode.split(''))
      submitCodeAndTriggerAutoSubmit(fullCode)
      return
    }

    const cursorPosition = cursorPositionRef.current[index]
    const isHighlighted = code[index] && cursorPosition.start !== cursorPosition.end

    // Handle user input to overwrite existing value
    if (inputRefs.current[index] && code[index] && text.length === 2 && !isHighlighted) {
      handleOverwrite(text, index)
      return
    }

    // Handle paste
    if (text.length > 1) {
      if (code[index] && !isHighlighted) {
        // If cursor is at the beginning of the input, remove the first character since it's the existing value
        if (cursorPosition.start === 0) {
          text = text.slice(0, -1)
        } else if (cursorPosition.start === 1) {
          // If cursor is at the end of the input, remove the last character since it's the existing value
          text = text.slice(1)
        }
      }
      handlePaste(text, index)
      return
    }

    const newCode = [...code]
    newCode[index] = text
    updateCode(newCode, index)
  }

  const handleKeyPress = (event: any, index: number) => {
    if (event.nativeEvent.key === 'Backspace') {
      if (index > 0) {
        inputRefs.current[index - 1]?.focus()
      }
      const newCode = [...code]
      newCode[index] = ''
      setCode(newCode)
      onCodeChange?.(newCode.join(''))
    }
  }

  const handleSelectionChange = (index: number) => (event: NativeSyntheticEvent<TextInputSelectionChangeEventData>) => {
    const { start, end } = event.nativeEvent.selection
    cursorPositionRef.current[index] = { start, end }
  }

  return (
    <Container testID={tID('CodeInput')}>
      <LabelContainer>
        <BodyText color={error || codeStatus ? colors.textError : undefined}>
          {label}
          {/*eslint-disable-next-line formatjs/no-literal-string-in-jsx*/}
          {(Boolean(error) || Boolean(codeStatus)) && '*'}
        </BodyText>
      </LabelContainer>
      <InputContainer>
        {Array.from({ length }).map((_, index) => (
          <StyledTextInput
            aria-label={formatMessage(
              {
                defaultMessage: 'Code input {index} of {length}',
                description: 'Label for code input containing several input boxes for each character',
              },
              { index, length },
            )}
            aria-labelledby={tID(`field-${name}-${index}`)}
            error={Boolean(error) || Boolean(codeStatus)}
            id={tID(`field-${name}-${index}`)}
            isFocused={focusedIndex === index}
            key={index}
            keyboardType='default'
            autoCapitalize='characters'
            placeholder=''
            ref={(ref) => (inputRefs.current[index] = ref)}
            returnKeyType='done'
            style={
              [
                fontStyles[TextType.BODY][BodyTextSize.DEFAULT],
                { lineHeight: IS_WEB ? 24 : 20 },
              ] as StyleProp<TextStyle>
            }
            testID={tID(`field-${name}-${index}`)}
            value={code[index] || ''}
            onBlur={() => setFocusedIndex(null)}
            onChangeText={(text) => handleChange(text, index)}
            onFocus={() => setFocusedIndex(index)}
            onKeyPress={(event) => handleKeyPress(event, index)}
            onSelectionChange={handleSelectionChange(index)}
            selectTextOnFocus={true}
            onLayout={
              index === 0
                ? () => {
                    if (!inputFocusRef.current) {
                      inputFocusRef.current = true
                      inputRefs.current[0]?.focus()
                    }
                  }
                : undefined
            }
            textContentType='oneTimeCode'
          />
        ))}
      </InputContainer>
      {(Boolean(error) || Boolean(codeStatus)) && (
        <ErrorContainer>
          <AlertLoudIcon fillColor={colors.iconError} />
          <ErrorText
            accessibilityLabel={codeStatus ? formatMessage(CODE_MESSAGES[codeStatus]) : error}
            accessibilityRole={AccessibilityRolesNative.ALERT}
            color={colors.textError}
            nativeID={tID(`field-${name}-error-message`)}
            size={Size.SMALL}
            testID={tID(`field-${name}-error-message`)}
            text={codeStatus ? formatMessage(CODE_MESSAGES[codeStatus]) : error}
          />
        </ErrorContainer>
      )}
    </Container>
  )
}

export const CodeInputRFF: FunctionComponent<FieldRenderProps<string>> = ({
  codeStatus,
  handleSubmit,
  input: { name, value, onChange },
  label,
  length,
  meta: { touched, error },
  shouldAutoSubmit,
  onAutoSubmit,
}) => {
  return (
    <CodeInput
      codeStatus={codeStatus}
      error={touched && error ? error : undefined}
      label={label}
      length={length}
      name={name}
      onCodeChange={onChange}
      onSubmit={handleSubmit}
      shouldAutoSubmit={shouldAutoSubmit}
      value={value}
      onAutoSubmit={onAutoSubmit}
    />
  )
}
